import proj4 from 'proj4';

const registeredProjections: Map<string, any> = new Map();

proj4.defs('EPSG:4326', '+proj=longlat +datum=WGS84 +no_defs');
proj4.defs('EPSG:3857', '+proj=merc +a=6378137 +b=6378137 +lat_ts=0 +lon_0=0 +x_0=0 +y_0=0 +k=1 +units=m +nadgrids=@null +wktext +no_defs');
proj4.defs('EPSG:32643', '+proj=utm +zone=43 +datum=WGS84 +units=m +no_defs');
proj4.defs('EPSG:4978', '+proj=geocent +datum=WGS84 +units=m +no_defs');
proj4.defs('EPSG:3395', '+proj=merc +lon_0=0 +k=1 +x_0=0 +y_0=0 +datum=WGS84 +units=m +no_defs');

export class Projection {
  public code: string;
  public units: string;

  constructor(code: string, units: string = 'degrees') {
    this.code = code;
    this.units = units;
  }

  static get(code: string): Projection {
    if (registeredProjections.has(code)) {
      return registeredProjections.get(code);
    }
    const proj = new Projection(code);
    registeredProjections.set(code, proj);
    return proj;
  }

  static register(code: string, options: { proj4: string; extent?: number[]; units?: string }): void {
    proj4.defs(code, options.proj4);
    const proj = new Projection(code, options.units || 'meters');
    registeredProjections.set(code, proj);
  }

  static transform(coord: [number, number], from: string, to: string): [number, number] {
    return proj4(from, to, [...coord]) as [number, number];
  }

  static transformExtent(
    extent: { minX: number; minY: number; maxX: number; maxY: number },
    from: string,
    to: string
  ): { minX: number; minY: number; maxX: number; maxY: number } {
    const bl = proj4(from, to, [extent.minX, extent.minY]) as [number, number];
    const tr = proj4(from, to, [extent.maxX, extent.maxY]) as [number, number];
    return { minX: bl[0], minY: bl[1], maxX: tr[0], maxY: tr[1] };
  }
}

export class CoordinateFormatter {
  static toDMS(coord: [number, number]): string {
    const lat = CoordinateFormatter._toDMSPart(coord[1], 'N', 'S');
    const lon = CoordinateFormatter._toDMSPart(coord[0], 'E', 'W');
    return `${lat} ${lon}`;
  }

  private static _toDMSPart(value: number, posDir: string, negDir: string): string {
    const dir = value >= 0 ? posDir : negDir;
    const abs = Math.abs(value);
    const deg = Math.floor(abs);
    const minFloat = (abs - deg) * 60;
    const min = Math.floor(minFloat);
    const sec = ((minFloat - min) * 60).toFixed(2);
    return `${deg}\u00b0${min}'${sec}"${dir}`;
  }

  static toUTM(coord: [number, number]): string {
    const zone = Math.floor((coord[0] + 180) / 6) + 1;
    const band = coord[1] >= 0 ? 'N' : 'S';
    const utmProj = `+proj=utm +zone=${zone} +${coord[1] >= 0 ? 'north' : 'south'} +datum=WGS84 +units=m +no_defs`;
    try {
      const result = proj4('EPSG:4326', utmProj, [...coord]) as [number, number];
      return `${zone}${band} ${Math.round(result[0])} ${Math.round(result[1])}`;
    } catch {
      return `${zone}${band} - -`;
    }
  }

  static toMGRS(coord: [number, number], precision: number = 5): string {
    const zone = Math.floor((coord[0] + 180) / 6) + 1;
    const band = coord[1] >= 0 ? 'N' : 'S';
    return `${zone}${band} ${coord[0].toFixed(precision)} ${coord[1].toFixed(precision)}`;
  }

  static toDecimalDegrees(coord: [number, number], precision: number = 6): string {
    return `${coord[1].toFixed(precision)}, ${coord[0].toFixed(precision)}`;
  }
}
